import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from '@/lib/toast'
import i18n from '@/i18n'
import { api } from '@/lib/api'
import type {
  InteractionTemplate,
  InteractionTemplateListResponse,
  InteractionTemplatePreset,
  InteractionType,
  InteractionQuality,
} from '@/lib/types'

export const DEFAULT_TEMPLATES: InteractionTemplatePreset[] = [
  {
    id: 'preset-coffee',
    name: 'Had coffee',
    type: 'coffee',
    quality: 'good',
    icon: '☕',
    isPreset: true,
  },
  {
    id: 'preset-call',
    name: 'Quick call',
    type: 'call',
    quality: 'good',
    icon: '📞',
    isPreset: true,
  },
  {
    id: 'preset-lunch',
    name: 'Lunch',
    type: 'dinner',
    quality: 'good',
    icon: '🍽️',
    isPreset: true,
  },
  {
    id: 'preset-birthday',
    name: 'Birthday dinner',
    type: 'dinner',
    quality: 'great',
    icon: '🎂',
    isPreset: true,
  },
  {
    id: 'preset-video',
    name: 'Video call',
    type: 'video_call',
    quality: 'good',
    icon: '📹',
    isPreset: true,
  },
  {
    id: 'preset-text',
    name: 'Text catch-up',
    type: 'text',
    quality: 'neutral',
    icon: '💬',
    isPreset: true,
  },
  {
    id: 'preset-group',
    name: 'Group hangout',
    type: 'meeting',
    quality: 'great',
    icon: '👥',
    isPreset: true,
  },
  {
    id: 'preset-work',
    name: 'Work meeting',
    type: 'meeting',
    quality: 'neutral',
    icon: '💼',
    isPreset: true,
  },
]

export function useInteractionTemplates() {
  return useQuery({
    queryKey: ['interaction-templates'],
    queryFn: () => api<InteractionTemplateListResponse>('/api/interaction-templates'),
  })
}

export function useCreateInteractionTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      name: string
      type: InteractionType
      quality?: InteractionQuality | null
      location?: string | null
      icon?: string | null
      sort_order?: number
    }) =>
      api<InteractionTemplate>('/api/interaction-templates', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['interaction-templates'] })
      toast.success(i18n.t('toast.templateCreated'))
    },
    onError: () => {
      toast.error(i18n.t('toast.error'))
    },
  })
}

export function useUpdateInteractionTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & Record<string, unknown>) =>
      api<InteractionTemplate>(`/api/interaction-templates/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['interaction-templates'] })
      toast.success(i18n.t('toast.templateUpdated'))
    },
    onError: () => {
      toast.error(i18n.t('toast.error'))
    },
  })
}

export function useDeleteInteractionTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api<void>(`/api/interaction-templates/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['interaction-templates'] })
      toast.success(i18n.t('toast.templateDeleted'))
    },
    onError: () => {
      toast.error(i18n.t('toast.error'))
    },
  })
}
